<template>
  <div
    id="toolbox"
    ref="root"
    :class="[themeClass, { disabled, 'is-fish': isFish }]"
    :aria-disabled="disabledTag"
    data-test="map-toolbox"
  >
    <h3>Tools</h3>
    <ul id="map-tools" data-test="map-tools">
      <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions -->
      <li
        id="add-route"
        :class="[
          'tool-item',
          {
            selected: selected === 'route' || routeBuilderIntroHighlight,
            'disabled-tool': toolsDisabled,
          },
        ]"
        data-tool-tip-target="control-build-route"
        data-test="map-tool-add-route"
        @click="draw($event, 'route', MARKUP_CREATE_ORIGINS.ROUTE_TOOLS)"
      >
        <div class="tool-icon">
          <x-tool-tip>Build Route</x-tool-tip>
          <x-icon icon="routing/route" />
        </div>
        <div class="tool-label">Build Route</div>
      </li>

      <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions -->
      <li
        id="add-line-string"
        :class="[
          'tool-item',
          { selected: selected === 'line', 'disabled-tool': toolsDisabled },
        ]"
        data-test="map-tool-add-line-string"
        @click="draw($event, 'line', MARKUP_CREATE_ORIGINS.LINE_TOOLS)"
      >
        <div class="tool-icon">
          <x-tool-tip>Draw Line / Measure Distance</x-tool-tip>
          <x-icon icon="add-line" />
        </div>
        <div class="tool-label">Line Distance</div>
      </li>

      <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions -->
      <li
        id="add-shape"
        :class="[
          'tool-item',
          { selected: selected === 'shape', 'disabled-tool': toolsDisabled },
        ]"
        data-test="map-tool-add-shape"
        @click="draw($event, 'shape', MARKUP_CREATE_ORIGINS.AREA_TOOLS)"
      >
        <div class="tool-icon">
          <x-tool-tip>Draw Shape / Measure Area</x-tool-tip>
          <x-icon icon="add-shape" />
        </div>
        <div class="tool-label">Area Shape</div>
      </li>

      <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions -->
      <li
        id="add-point"
        :class="[
          'tool-item',
          { selected: selected === 'point', 'disabled-tool': toolsDisabled },
        ]"
        data-test="map-tool-add-point"
        data-tool-tip-target="add-waypoint"
        @click="draw($event, 'point', MARKUP_CREATE_ORIGINS.WAYPOINT_TOOLS)"
      >
        <div class="tool-icon">
          <x-tool-tip>Add Waypoint</x-tool-tip>
          <x-icon icon="add-waypoint" />
        </div>
        <div class="tool-label">Add Waypoint</div>
      </li>

      <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions -->
      <li
        v-if="showAddPhoto"
        id="add-photo"
        :class="[
          'tool-item',
          { selected: selected === 'photo', 'disabled-tool': toolsDisabled },
        ]"
        data-test="map-tool-add-photo"
        @click="draw($event, 'photo', MARKUP_CREATE_ORIGINS.PHOTO_TOOLS)"
      >
        <div class="tool-icon">
          <x-tool-tip>Add Photo Waypoint</x-tool-tip>
          <x-icon icon="add-photo" />
        </div>
        <div class="tool-label">Add Photo</div>
      </li>

      <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions -->
      <li
        v-if="showWindCalendarTool"
        id="open-wind-calendar"
        :class="[
          'tool-item',
          {
            selected: selected === 'wind-calendar',
            'disabled-tool': toolsDisabled,
          },
        ]"
        data-test="map-tool-wind-calendar"
        @click="openWindCalendar"
      >
        <div class="tool-icon">
          <x-tool-tip>Open Wind Calendar</x-tool-tip>
          <x-icon
            icon="weather/currently/wind"
            width="20"
            height="20"
            color="var(--ys-color-icon-primary)"
          />
        </div>
        <div class="tool-label">Open Wind Calendar</div>
      </li>
    </ul>
  </div>
</template>
<!-- eslint-disable vue/block-lang -->
<script>
/* eslint-disable vue/component-api-style */
import pubsub from '@/lib/pubsub'
import { getThemeClasses } from '@/lib/theme-utils'

import subscriptionService from '@/services/subscription-service'
import { camera, mapReadyPromise } from '@/services/map-service'

import { offlineServiceState } from '@/services/offline-map-service'

import xIcon from '@/components/shared/x-icon'
import xToolTip from '@/components/shared/x-tool-tip'
import Paywall from '@/components/paywall'
import { MARKUP_CREATE_ORIGINS, WIND_EVENTS } from '@/lib/enums-marketing'
import marketingEventService from '@/services/marketing-event-service'

export default {
  name: 'ControlToolbox',

  components: {
    xIcon,
    xToolTip,
  },

  inject: {
    theme: { default: 'hunt' },
  },

  data() {
    return {
      selected: null,
      routeBuilderIntroHighlight: false,
      MARKUP_CREATE_ORIGINS,
    }
  },

  computed: {
    isFish() {
      return true
    },
    themeClass() {
      return getThemeClasses(this.theme)
    },
    camera,
    disabled() {
      return offlineServiceState.inCreateMode
    },
    disabledTag() {
      return this.disabled ? true : null
    },
    toolsDisabled() {
      return [
        `fish-route-create`,
        `fish-route-edit`,
        `fish-waypoint-create`,
        `fish-waypoint-edit`,
        `fish-waypoint-photo-quick-add`,
        `fish-line-create`,
        `fish-line-edit`,
        `fish-shape-create`,
        `fish-shape-edit`,
      ].includes(this.$route?.name)
    },
    showAddPhoto() {
      return true
    },
    showWindCalendarTool() {
      return false
    },
  },

  watch: {
    $route: {
      handler: function (to) {
        switch (to?.name) {
          case `fish-route-create`:
            this.selected = 'route'
            break
          case `fish-waypoint-create`:
            this.selected = 'point'
            break
          case `fish-line-create`:
            this.selected = 'line'
            break
          case `fish-shape-create`:
            this.selected = 'shape'
            break
          case `fish-waypoint-photo-quick-add`:
            this.selected = 'photo'
            break
          case 'wind-calendar-card-hunt':
            this.selected = 'wind-calendar'
            break
          default:
            this.selected = null
        }
      },
      immediate: true,
    },
  },

  mounted() {
    this.$refs.root.component = this

    this.startDrawing = this.startDrawing.bind(this)

    // Listen for when the user decides to start drawing from the rich content
    // card
    this.startDrawingAtCoordHandler = this.startDrawingAtCoord.bind(this)
    pubsub.subscribe('queryPoint', this.startDrawingAtCoordHandler)
    pubsub.subscribe('queryPhoto', this.startDrawingAtCoordHandler)
    pubsub.subscribe('openRouteBuilderIntro', (event) => {
      this.draw(event, 'route')
      pubsub.publish('show-route-with-tour')
    })

    pubsub.subscribe('routeBuilderIntroDisplayed', (event) => {
      this.routeBuilderIntroHighlight = event
    })
  },

  /* This isn't called due to keep-alive, but good habit to unmount
      subscriptions */
  unmounted() {
    pubsub.unsubscribe('queryPoint', this.startDrawingAtCoordHandler)
    pubsub.unsubscribe('queryPhoto', this.startDrawingAtCoordHandler)
    pubsub.unsubscribe('openRouteBuilderIntro')
    pubsub.unsubscribe('routeBuilderIntroDisplayed')
    pubsub.publish('endRouteTour')
  },

  methods: {
    /**
     * Starts the draw event for a waypoint at a given point. Must separate
     * from the original startDrawing function to support startDrawing without
     * a given coordinate. This is currently only used for dropping a photo
     * or point at selected route with rich content.
     *
     * @param options - Event options object
     * @param options.evt - Event object from the clicked object
     * @param options.coord - Optional coordinate to draw at.
     * @param options.origin - Origin for marketing events
     */
    startDrawingAtCoord({ evt, coord, origin }) {
      this.startDrawing(evt, coord, origin)
    },

    /**
     * Click handler for starting the drawing
     *
     * @param event - Event object
     * @param type - Type of drawing to start
     * @param [origin] - Origin for marketing events
     */
    draw(event, type, origin = '') {
      this.startDrawing(event, null, origin)
      this.selected = type
    },

    /**
     * Starts the draw event for the selected tool
     *
     * @param evt - Event object from the clicked object
     * @param [coord] - Coordinate where to start drawing (for waypoints)
     * @param [origin] - Origin for marketing events
     */
    startDrawing(evt, coord, origin = '') {
      const itemToDraw = evt.currentTarget
        ? evt.currentTarget.id
        : evt.target.id
      this.newMarkupForDrawing(itemToDraw, coord, origin)
    },

    /**
     * Create new markup for drawing
     *
     * @param itemToDraw - Item that we should start drawing
     * @param [coord] - Coordinate where to start drawing (for waypoints)
     * @param [origin] - Origin for marketing events
     */
    async newMarkupForDrawing(itemToDraw, coord, origin = '') {
      await mapReadyPromise
      const { visibleCenter: center } = this.camera
      coord ??= center

      switch (itemToDraw) {
        case 'rc-add-point':
        case 'add-point': {
          if (!coord) {
            throw new Error('Coordinate is required to create a point')
          }
          this.$router.push({
            name: `fish-waypoint-create`,
            query: {
              location: coord.toURLCoordParam(),
            },
            state: {
              origin,
            },
          })
          break
        }
        case 'add-route': {
          this.$router.push({
            name: `fish-route-create`,
            state: { origin },
          })
          break
        }
        case 'add-route-from-point': {
          this.$router.push({
            name: `fish-route-create`,
            state: { origin },
            query: { from: coord?.toURLCoordParam() },
          })
          break
        }
        case 'add-line-string':
          this.$router.push({
            name: `fish-line-create`,
            state: { origin },
          })
          break
        case 'add-shape':
          this.$router.push({
            name: `fish-shape-create`,
            state: { origin },
          })
          break
        case 'add-status':
          this.$router.push({ name: `fish-add-trail-status` })
          break
        case 'rc-add-photo':
        case 'add-photo': {
          if (!coord) {
            throw new Error('Coordinate is required to create a point')
          }
          if (!subscriptionService.hasActiveSubscription()) {
            // if basic user, show paywall
            return Paywall.displayBasicPayWallMessage('photoWaypoint')
          }
          this.$router.push({
            name: `fish-waypoint-photo-quick-add`,
            query: {
              location: coord.toURLCoordParam(),
            },
            state: { origin },
          })
          break
        }
      }
    },

    /**
     * Disables click events on tools
     */
    // eslint-disable-next-line vue/no-unused-properties
    disableTools() {
      const toolList = this.$el.querySelectorAll('#map-tools li')
      for (let i = 0, l = toolList.length; i < l; i++) {
        toolList[i].classList.add('disabled-tool')
      }
    },

    /**
     * Enables click events on tools
     */
    // eslint-disable-next-line vue/no-unused-properties
    enableTools() {
      const toolList = this.$el.querySelectorAll('#map-tools li')
      for (let i = 0, l = toolList.length; i < l; i++) {
        toolList[i].classList.remove('disabled-tool')
      }
      this.selected = null
    },
    openWindCalendar() {
      marketingEventService.publish(WIND_EVENTS.WIND_CALENDAR_VIEWED, {
        origin: 'tools',
        type: 'comparison',
      })

      this.selected = 'wind-calendar'
      this.$router.push({ name: 'wind-calendar-card-hunt' }).catch(() => {})
    },
  },
}
</script>

<style lang="scss" scoped>
@use 'sass:map';
@use '@/styles/system/_themes.scss';
@use '@/styles/system/_shadow.scss';
@use '@/styles/system/_elements.scss';
@use '@/styles/system/_breakpoints.scss';

// animation for live map active indicator
@keyframes pulse {
  from {
    background-color: rgb(0, 155, 0);
  }
  50% {
    background-color: rgb(0, 236, 0);
  }
  to {
    background-color: rgb(0, 155, 0);
  }
}

#map-tools {
  list-style-type: none;
  margin: 0;
  padding: 0;
  .tool-icon {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

#toolbox {
  position: relative;
  background: var(--ys-color-surface-base);
  border-radius: 10px;
  width: 45px;
  box-shadow: shadow.$Low;

  &.disabled::before {
    @include elements.disabledOverlay();

    border-radius: 10px;
  }

  h3,
  li {
    height: 35px;
    width: 35px;
    margin: 0 auto;
    align-items: center;
    justify-content: center;
    display: flex;
  }

  h3 {
    font-size: 11px;
    font-weight: 500;
    margin-bottom: 0;
  }

  li {
    font-size: 0;
    margin-bottom: 6px;
    cursor: pointer;
    border-radius: 7px;
    position: relative;

    &:hover,
    &.selected {
      background: var(--ys-color-surface-primary);
    }

    &.disabled-tool {
      pointer-events: none;
    }

    svg {
      width: 14px;
    }
  }

  @include breakpoints.laptop-height {
    display: flex;
    width: unset;
    padding: 2px 8px;
    h3 {
      transform: rotate(270deg);
      width: unset;
    }
    #map-tools {
      display: flex;
      li {
        margin-bottom: 0;
      }
    }
  }
  @include breakpoints.mobile {
    display: flex;
    flex-direction: column;
    top: 0;
    &.is-fish {
      top: 50px;
    }
    @include breakpoints.app-prototyping() {
      display: none;
    }
    @include breakpoints.laptop-height {
      flex-direction: row;
    }
  }
}

@media print {
  #toolbox {
    display: none;
  }
}
</style>
